// Web Audio based AudioManager. One AudioContext, decoded buffers cached.
// Music plays via BufferSource for sample-accurate timing.
export class AudioManager {
  readonly ctx: AudioContext;
  private musicGain: GainNode;
  private sfxGain: GainNode;
  private bufferCache = new Map<string, Promise<AudioBuffer>>();

  // Current real music source + its dedicated gain (used for fade in/out).
  private musicSource: AudioBufferSourceNode | null = null;
  private musicSourceGain: GainNode | null = null;
  private musicStartedAt = 0; // ctx.currentTime when playback began
  private musicOffsetSec = 0; // start offset within the buffer
  private musicBuffer: AudioBuffer | null = null;

  // Idle background loop. The source keeps running continuously once started
  // (so playback position is preserved across transitions) — we only fade
  // its gain to 0 / back to 1 when real music takes over and releases.
  private idleBuffer: AudioBuffer | null = null;
  private idleSource: AudioBufferSourceNode | null = null;
  private idleGain: GainNode;

  // Length of every BGM crossfade, in seconds.
  private static readonly FADE_SEC = 0.2;

  constructor() {
    this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)({ latencyHint: 'interactive' });
    this.musicGain = this.ctx.createGain();
    this.sfxGain = this.ctx.createGain();
    this.musicGain.gain.value = 0.85;
    this.sfxGain.gain.value = 0.9;
    this.musicGain.connect(this.ctx.destination);
    this.sfxGain.connect(this.ctx.destination);
    this.idleGain = this.ctx.createGain();
    this.idleGain.gain.value = 1;
    this.idleGain.connect(this.musicGain);
  }

  async unlock() {
    if (this.ctx.state === 'suspended') await this.ctx.resume();
  }

  setMusicVolume(v: number) { this.musicGain.gain.value = Math.max(0, Math.min(1, v)); }
  setSfxVolume(v: number) { this.sfxGain.gain.value = Math.max(0, Math.min(1, v)); }

  async load(url: string): Promise<AudioBuffer> {
    let p = this.bufferCache.get(url);
    if (p) return p;
    p = (async () => {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`audio fetch ${url}: ${res.status}`);
      const buf = await res.arrayBuffer();
      return await this.ctx.decodeAudioData(buf);
    })();
    this.bufferCache.set(url, p);
    try { return await p; }
    catch (e) { this.bufferCache.delete(url); throw e; }
  }

  async playSfx(url: string, opts: { volume?: number } = {}) {
    if (!url) return;
    let buf: AudioBuffer;
    try { buf = await this.load(url); }
    catch (e) { console.warn('sfx load fail', url, e); return; }
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    if (opts.volume !== undefined) {
      const g = this.ctx.createGain();
      g.gain.value = opts.volume;
      src.connect(g).connect(this.sfxGain);
    } else {
      src.connect(this.sfxGain);
    }
    src.start();
  }

  /** Stop music if playing. Crossfades back to the idle BGM when one is registered. */
  stopMusic() {
    this.fadeOutCurrentMusic(this.ctx.currentTime);
    this.fadeIdleTo(1, this.ctx.currentTime);
  }

  /** Start a buffer as music at audioContext time = whenCtxTime (>= ctx.currentTime).
   *  Crossfades over FADE_SEC against any current music and the idle BGM. */
  startMusic(buffer: AudioBuffer, whenCtxTime: number, offsetSec = 0, loop = false) {
    const fadeStart = Math.max(this.ctx.currentTime, whenCtxTime);
    this.fadeOutCurrentMusic(fadeStart);
    this.fadeIdleTo(0, fadeStart);

    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = loop;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, fadeStart);
    g.gain.linearRampToValueAtTime(1, fadeStart + AudioManager.FADE_SEC);
    src.connect(g).connect(this.musicGain);
    this.musicSource = src;
    this.musicSourceGain = g;
    this.musicBuffer = buffer;
    this.musicStartedAt = whenCtxTime;
    this.musicOffsetSec = offsetSec;
    src.start(whenCtxTime, offsetSec);
  }

  /** Immediately silence the idle BGM without any fade (used on hard cuts like
   *  entering gameplay where the song should kick in cleanly). The idle source
   *  is kept alive so playback position is still preserved. */
  muteIdleBgm() {
    const now = this.ctx.currentTime;
    this.idleGain.gain.cancelScheduledValues(now);
    this.idleGain.gain.setValueAtTime(0, now);
  }

  /** Register a looping ambient loop to play whenever no real music is active.
   *  The source is started once and kept alive so its position is preserved. */
  setIdleBgm(buffer: AudioBuffer | null) {
    this.idleBuffer = buffer;
    if (!buffer) { this.stopIdleBgm(); return; }
    this.startIdleBgm();
    // Match current state: if real music is active, idle should be silent.
    this.idleGain.gain.cancelScheduledValues(this.ctx.currentTime);
    this.idleGain.gain.setValueAtTime(this.musicSource ? 0 : 1, this.ctx.currentTime);
  }

  private startIdleBgm() {
    if (!this.idleBuffer || this.idleSource) return;
    const src = this.ctx.createBufferSource();
    src.buffer = this.idleBuffer;
    src.loop = true;
    src.connect(this.idleGain);
    this.idleSource = src;
    src.start();
  }

  private stopIdleBgm() {
    if (!this.idleSource) return;
    try { this.idleSource.stop(); } catch { /* ignore */ }
    try { this.idleSource.disconnect(); } catch { /* ignore */ }
    this.idleSource = null;
  }

  private fadeIdleTo(target: number, startCtxTime: number) {
    if (!this.idleBuffer) return;
    // Make sure the idle source is alive so it can be heard once the gain rises.
    if (target > 0) this.startIdleBgm();
    const startT = Math.max(this.ctx.currentTime, startCtxTime);
    const g = this.idleGain.gain;
    g.cancelScheduledValues(this.ctx.currentTime);
    // Anchor the current value, then ramp from startT.
    g.setValueAtTime(g.value, this.ctx.currentTime);
    g.setValueAtTime(g.value, startT);
    g.linearRampToValueAtTime(target, startT + AudioManager.FADE_SEC);
  }

  /** Fade out + dispose whatever real-music source is currently active.
   *  The fade begins at startCtxTime and runs FADE_SEC long; the source
   *  is scheduled to stop slightly after the ramp completes. */
  private fadeOutCurrentMusic(startCtxTime: number) {
    const src = this.musicSource;
    const g = this.musicSourceGain;
    this.musicSource = null;
    this.musicSourceGain = null;
    this.musicBuffer = null;
    if (!src || !g) return;
    const startT = Math.max(this.ctx.currentTime, startCtxTime);
    const endT = startT + AudioManager.FADE_SEC;
    g.gain.cancelScheduledValues(this.ctx.currentTime);
    g.gain.setValueAtTime(g.gain.value, this.ctx.currentTime);
    g.gain.setValueAtTime(g.gain.value, startT);
    g.gain.linearRampToValueAtTime(0, endT);
    try { src.stop(endT + 0.05); } catch { /* ignore */ }
    const cleanupMs = (endT - this.ctx.currentTime + 0.2) * 1000;
    setTimeout(() => {
      try { src.disconnect(); } catch { /* ignore */ }
      try { g.disconnect(); } catch { /* ignore */ }
    }, Math.max(0, cleanupMs));
  }

  /** Music time in seconds since the music started (may be negative before whenCtxTime). */
  musicTime(): number {
    if (!this.musicSource) return 0;
    return this.ctx.currentTime - this.musicStartedAt + this.musicOffsetSec;
  }

  /**
   * Music time in seconds at the moment a DOM event occurred (perfMs = performance.now() ms).
   * Uses the perf<->ctx clock offset measured at "now" — sub-ms accurate for inputs that
   * fire within the same animation frame.
   */
  musicTimeAtPerf(perfMs: number): number {
    if (!this.musicSource) return 0;
    const ctxNowSec = this.ctx.currentTime;
    const perfNowSec = performance.now() / 1000;
    const ctxAtEvent = ctxNowSec + (perfMs / 1000 - perfNowSec);
    return ctxAtEvent - this.musicStartedAt + this.musicOffsetSec;
  }

  isPlaying() { return this.musicSource !== null; }
  durationSec() { return this.musicBuffer?.duration ?? 0; }
}
